// The project head: reading `projects/<name>/project.md` into the four facts the
// dashboard draws, and turning a date into the string that goes beside it.
//
// Pure, and here rather than in the component, for the same reason sessions.ts and
// wiki.ts are: `pnpm test` can cover it without rendering anything, and the parsing
// is the part where being quietly wrong is invisible on screen.
//
// This is a *strict* reader of a small format, and that is deliberate. The database's
// own convention (see `CLAUDE.md` in ~/fian, "Project shape") is one key per line,
// two-space indents, `- ` for list items — no anchors, no flow sequences, no
// multi-line scalars. A real YAML parser would be a dependency earning its keep on
// syntax nobody writes here. What matters much more is the failure mode: anything
// this can't read comes back absent, the section simply doesn't render, and the
// morning digest — which reads the same file with a model rather than a parser —
// still catches the commitment. A visibly missing card beats a confidently wrong one.

import type { TreeNode } from './wiki';

export type ProjectDate = {
  date: string;
  what: string;
  /** A date that exists but isn't locked in. The obligation is to confirm it. */
  tentative: boolean;
};

export type ProjectCommitment = {
  what: string;
  /** Blocked until this date — can't be scheduled before it, so it isn't nagged. */
  dormantUntil: string | null;
};

export type Project = {
  /** The `# ` heading, falling back to the directory name. */
  title: string;
  /** Null is a real state, not a parse failure: a project with no milestone set. */
  milestone: string | null;
  repos: string[];
  dates: ProjectDate[];
  scheduling: ProjectCommitment[];
  /**
   * A project that exists to be photographed, not worked on.
   *
   * The screenshots need a project holding states no real one does for long —
   * a date already past, a tentative one, a dormant commitment — because those
   * are relative to today and a real project drifts out of them within a week.
   * The flag keeps it out of the two places that put projects in front of you,
   * the board and the sidebar, while leaving it a working address for a shot to
   * point at. It is presentation, the same as `hidden` in lib/wiki.ts: the file
   * is in the repo and anything reading the repo will find it.
   */
  fixture: boolean;
  /** Whatever prose sits under the frontmatter. Usually nothing. */
  body: string;
};

type Scalar = string | boolean;
type Node = Scalar | Record<string, Scalar>;

/**
 * Split `---\n…\n---\n` off the front of a document.
 *
 * The fence has to be the very first thing in the file. A `---` further down is a
 * horizontal rule in somebody's prose, and treating it as a delimiter would eat the
 * top of the document.
 */
export function splitFrontmatter(text: string): { front: string; body: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(text);
  if (!m) return { front: '', body: text };
  return { front: m[1], body: text.slice(m[0].length) };
}

/** `"a: b"` → `a: b`, `true` → true, everything else as typed. */
function scalar(raw: string): Scalar {
  const s = raw.trim();
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s.length > 1 && ((s[0] === '"' && s.endsWith('"')) || (s[0] === "'" && s.endsWith("'")))) {
    const inner = s.slice(1, -1);
    return s[0] === '"' ? inner.replace(/\\(["\\])/g, '$1') : inner;
  }
  return s;
}

const KEY = /^([A-Za-z_][A-Za-z0-9_]*):[ \t]*(.*)$/;

/**
 * The subset described at the top of this file, one pass, no recursion.
 *
 * Blank lines and `#` comments are skipped anywhere. A line that matches nothing is
 * skipped too rather than throwing — a stray character in a hand-edited file should
 * cost that one line, not the whole head.
 */
export function parseFrontmatter(front: string): Record<string, Scalar | Node[]> {
  const out: Record<string, Scalar | Node[]> = {};
  const lines = front.split(/\r?\n/);
  let key: string | null = null;
  let list: Node[] | null = null;

  for (const line of lines) {
    if (!line.trim() || /^\s*#/.test(line)) continue;

    const top = KEY.exec(line);
    if (top && !/^\s/.test(line)) {
      key = top[1];
      if (top[2].trim()) {
        out[key] = scalar(top[2]);
        list = null;
      } else {
        // A key with nothing after the colon opens a list. `milestone:` with an empty
        // value is the database's way of writing "no milestone set", so an empty list
        // has to survive to the caller as exactly that rather than as a missing key.
        list = [];
        out[key] = list;
      }
      continue;
    }
    if (!list) continue;

    const item = /^\s+-\s*(.*)$/.exec(line);
    if (item) {
      const rest = item[1];
      const pair = KEY.exec(rest);
      if (pair) list.push({ [pair[1]]: scalar(pair[2]) });
      else if (rest.trim()) list.push(scalar(rest));
      continue;
    }
    // A continuation line: another key belonging to the map opened by the last `- `.
    const cont = KEY.exec(line.trim());
    const last = list[list.length - 1];
    if (cont && last && typeof last === 'object') last[cont[1]] = scalar(cont[2]);
  }
  return out;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'boolean' ? String(v) : '';
}

/** Read one `project.md`. `fallback` names the project when the file has no heading. */
export function parseProject(text: string, fallback: string): Project {
  const { front, body } = splitFrontmatter(text);
  const meta = parseFrontmatter(front);

  const heading = /^#[ \t]+(.+?)[ \t]*$/m.exec(body);
  const milestone = str(meta.milestone).trim();

  const list = (k: string): Node[] => (Array.isArray(meta[k]) ? (meta[k] as Node[]) : []);

  const dates: ProjectDate[] = [];
  for (const item of list('dates')) {
    if (typeof item !== 'object') continue;
    const date = str(item.date).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    dates.push({ date, what: str(item.what).trim(), tentative: item.tentative === true });
  }

  const scheduling: ProjectCommitment[] = [];
  for (const item of list('scheduling')) {
    const what = typeof item === 'object' ? str(item.what).trim() : str(item).trim();
    if (!what) continue;
    const dormant = typeof item === 'object' ? str(item.dormant_until).trim() : '';
    scheduling.push({
      what,
      dormantUntil: /^\d{4}-\d{2}-\d{2}$/.test(dormant) ? dormant : null,
    });
  }

  return {
    title: heading ? heading[1] : fallback,
    milestone: milestone || null,
    repos: list('repos').map(v => str(v).trim()).filter(Boolean),
    dates,
    scheduling,
    fixture: meta.fixture === true,
    body: heading ? body.slice(heading.index + heading[0].length).trim() : body.trim(),
  };
}

/**
 * Drop a leading `# Title` from a state file.
 *
 * Both halves of a project carry the same heading — `project.md` so the head can
 * read a display name, `state.md` so the file still stands alone in a terminal or
 * on GitHub. Rendered together that is the title twice, so the head keeps it and
 * the prose gives it up. Only a heading at the very top goes; a `# ` further down
 * belongs to the document.
 */
export function stripHeading(text: string): string {
  return text.replace(/^\s*#[ \t]+.+?[ \t]*(?:\r?\n|$)/, '').replace(/^\s+/, '');
}

/* ── Time ────────────────────────────────────────────────────────────────────
   Dates in this database are plain `YYYY-MM-DD` with no time and no zone — they
   mean a day, not an instant. So everything below works in UTC and compares whole
   days: parsing `2026-08-04` as local midnight and diffing against `Date.now()`
   is how a deadline reads as "tomorrow" east of Greenwich and "today" west of it. */

const DAY = 86_400_000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function utcDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Whole days from `today` to `iso`. Negative is the past. */
export function daysUntil(iso: string, today: Date): number {
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((utcDay(iso) - now) / DAY);
}

/**
 * `Tue Aug 4`, spelled out rather than left to `toLocaleDateString`.
 *
 * Same reason the tree skeleton's widths are fixed: a locale-dependent string is one
 * thing on the server and another in the browser, and React calls that a hydration
 * mismatch. This is also the format the morning digest mails, so a date reads
 * identically in the inbox and on the page.
 */
export function stamp(iso: string): string {
  const d = new Date(utcDay(iso));
  return `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** The countdown beside it: `4d`, `today`, `3d ago`. */
/**
 * The same day, for something that already happened: `Aug 9 2026`.
 *
 * Two differences from `stamp`, and both are about distance. The weekday is dropped
 * because it only helps for something you are going to be somewhere for, and the year
 * is added because a log is the one thing here that spans them — a completed-milestone
 * list is read for its trajectory, and "Aug 9" three entries down is unplaceable.
 */
export function stampWithYear(iso: string): string {
  const d = new Date(utcDay(iso));
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${d.getUTCFullYear()}`;
}

export function countdown(days: number): string {
  if (days === 0) return 'today';
  if (days > 0) return `${days}d`;
  return `${-days}d ago`;
}

/**
 * Past-due first and most-overdue at the top, then everything upcoming by how soon.
 *
 * A date still listed after its day has passed is the database's one failure signal —
 * the convention is that it comes out at the next merge, so its presence means
 * something slipped. It sorts to the top for the same reason the digest reports it.
 */
export function sortDates(dates: ProjectDate[], today: Date): ProjectDate[] {
  return [...dates].sort((a, b) => daysUntil(a.date, today) - daysUntil(b.date, today));
}

/** A dormant item is parked, not due: it drops below the ones actually waiting. */
export function isAwake(item: ProjectCommitment, today: Date): boolean {
  return !item.dormantUntil || daysUntil(item.dormantUntil, today) <= 0;
}

/* ── The log ─────────────────────────────────────────────────────────────────── */

/** One closed milestone: the day it closed, and what it was. */
export type ClosedMilestone = {
  date: string;
  what: string;
};

/**
 * `milestones.md`: one line per closed milestone, oldest first, `- <date> — <what>`.
 *
 * Read as loosely as the frontmatter is read strictly, and for the same reason in
 * reverse. The frontmatter drives a countdown, so a line it can't parse has to come
 * back absent rather than wrong. This file is prose that happens to be dated — the
 * only structure that matters is the day, and a line whose dash is a hyphen or whose
 * date got typed by hand should still show up in the log rather than vanish out of
 * it. Anything with no leading date at all is skipped, since there is nowhere to put
 * it; anything else keeps whatever follows the date.
 *
 * Returned newest first, which is the order it is read in and the reverse of the
 * order it is written in. The file accumulates downwards because appending is the
 * only write; the page is looked at from the top.
 */
export function parseMilestones(text: string): ClosedMilestone[] {
  const out: ClosedMilestone[] = [];
  for (const line of text.split('\n')) {
    const match = /^\s*[-*]\s*(\d{4}-\d{2}-\d{2})\s*(?:[—–-]\s*)?(.*)$/.exec(line);
    if (!match) continue;
    const what = match[2].trim();
    if (what) out.push({ date: match[1], what });
  }
  return out.reverse();
}

/* ── The tree ────────────────────────────────────────────────────────────────── */

/**
 * A directory is a project because it contains `project.md`, which is the database's
 * own rule rather than a path convention this app invented. That matters for two
 * cases it gets right for free: `archive/` needs no second rule, and a directory
 * that is half-migrated or has no head falls back to the plain file tree instead of
 * disappearing from the sidebar.
 */
export function isProject(node: TreeNode): boolean {
  return node.type === 'dir' && node.children.some(c => c.path.endsWith('/project.md'));
}

export function childNamed(node: TreeNode, name: string): TreeNode | null {
  return node.children.find(c => c.path.endsWith(`/${name}`)) ?? null;
}

/** Every markdown file under `notes/`, flattened — the shelf renders one level. */
export function notesOf(node: TreeNode): TreeNode[] {
  const dir = node.children.find(c => c.type === 'dir' && c.path.endsWith('/notes'));
  if (!dir) return [];
  const out: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.type === 'file') out.push(n);
      else walk(n.children);
    }
  };
  walk(dir.children);
  return out;
}
